// src/pages/admin/verificar-inmobiliarias-nuevas.tsx
import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

// Lista de agencias de los nuevos miembros (Noviembre 2025)
const AGENCIAS_NUEVOS_MIEMBROS = [
  'Enlace Comercial Inmobiliario',
  'Roberto García Rojas',
  'Ulysses Properties',
  'Mexpat Realtors',
  'Poncho Dávalos Real Estate',
  'Kara Realtors',
  'Donner y Asociados',
  'Moobi',
  'Paradise Ocean Properties',
  'GUGO Real Estate',
  'Discovery Vallarta Properties',
  'REMAX Kaizen',
  'Properties Vallarta',
  'Reálea',
  'Sí Real Estate',
  'Sol y Luna Realty',
  'North Shore Realty San Pancho',
  'Easy Home Vallarta',
  'Lloyd Real Estate',
  'Green Realty',
  'NuHome México',
  'Lemmus Real Estate',
  'Domus Fine Real Estate',
  'G&G Real Estate',
  'REMAX On the Bay',
  'SB Realtors',
  'Keller Williams Bahía',
  'Century 21 Ibarría Realtors',
  'North Shore Realty',
  'HOMIA',
  'BDB Realty',
  'Your Future Home International',
  'Century 21 CAM Grupo',
  'Daniel de Gandiaga Realtor',
  'Ojo de Agua Real Estate Group',
  'Pacific Bay Group',
  'Santiago Realtors',
  'Terra Magna Real Estate',
  'Andrea Pardio Realty',
  'Tania Montero',
  'eXp Realty México',
  'eXP Realty México',
  'HIR Casa',
  'Vida Vallarta Real Estate',
  'Destino Rentals',
  'Services Vallarta Real Estate',
  'Coastal Living Properties',
  'Inicio Real Estate',
  'Condominios en Puerto Vallarta',
  'Promotora de Desarollo Jarso',
  'Samperio Realty'
];

interface InmobiliariaFS {
  id: string;
  nombre: string;
  correo?: string;
  telefono?: string;
}

interface ResultadoVerificacion {
  agenciaNueva: string;
  existe: boolean;
  matchExacto: boolean;
  matchParcial: boolean;
  inmobiliariaEncontrada?: InmobiliariaFS;
  similares?: InmobiliariaFS[];
}

// Función para normalizar texto para comparación
const normalizar = (texto: string): string => {
  return texto
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    .replace(/[^a-z0-9\s]/g, '') // Solo letras, números y espacios
    .replace(/\s+/g, ' ')
    .trim();
};

// Función para calcular similitud entre dos strings
const calcularSimilitud = (str1: string, str2: string): number => {
  const s1 = normalizar(str1);
  const s2 = normalizar(str2);

  if (s1 === s2) return 1;

  // Verificar si uno contiene al otro
  if (s1.includes(s2) || s2.includes(s1)) return 0.8;

  // Verificar palabras en común
  const palabras1 = s1.split(' ').filter(p => p.length > 2);
  const palabras2 = s2.split(' ').filter(p => p.length > 2);
  const palabrasComunes = palabras1.filter(p => palabras2.some(p2 => p2.includes(p) || p.includes(p2)));

  if (palabrasComunes.length > 0) {
    return palabrasComunes.length / Math.max(palabras1.length, palabras2.length) * 0.7;
  }

  return 0;
};

export default function VerificarInmobiliariasNuevas() {
  const { user, loading } = useUser();
  const navigate = useNavigate();
  const [inmobiliariasFS, setInmobiliariasFS] = useState<InmobiliariaFS[]>([]);
  const [resultados, setResultados] = useState<ResultadoVerificacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState<'todos' | 'nuevas' | 'existentes' | 'similares'>('todos');
  const [creando, setCreando] = useState(false);
  const [progreso, setProgreso] = useState({ actual: 0, total: 0 });
  const [inmobiliariasCreadas, setInmobiliariasCreadas] = useState<string[]>([]);

  // Función para crear todas las inmobiliarias nuevas
  const crearInmobiliariasNuevas = async () => {
    const nuevasParaCrear = resultados.filter(r => !r.existe);

    // Filtrar duplicados (eXp vs eXP)
    const nombresUnicos = new Map<string, string>();
    nuevasParaCrear.forEach(r => {
      const normalizado = normalizar(r.agenciaNueva);
      if (!nombresUnicos.has(normalizado)) {
        nombresUnicos.set(normalizado, r.agenciaNueva);
      }
    });

    const inmobiliariasACrear = Array.from(nombresUnicos.values());

    const confirmacion = await Swal.fire({
      title: '¿Crear inmobiliarias?',
      html: `Se crearán <strong>${inmobiliariasACrear.length}</strong> nuevas inmobiliarias en Firestore.<br><br>
             <small class="text-muted">Nota: "eXp Realty México" y "eXP Realty México" se unifican en una sola.</small>`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, crear todas',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#0c2339'
    });

    if (!confirmacion.isConfirmed) return;

    setCreando(true);
    setProgreso({ actual: 0, total: inmobiliariasACrear.length });
    const creadas: string[] = [];
    const errores: string[] = [];

    for (let i = 0; i < inmobiliariasACrear.length; i++) {
      const nombre = inmobiliariasACrear[i];
      try {
        await addDoc(collection(db, 'inmobiliaria'), {
          nombre: nombre,
          descripcion: '',
          telefono: '',
          correo: '',
          direccion: '',
          años_experiencia: 0,
          redes: { facebook: '', instagram: '' },
          creadoDesde: 'migracion-nov-2025',
          createdAt: serverTimestamp()
        });
        creadas.push(nombre);
        setProgreso({ actual: i + 1, total: inmobiliariasACrear.length });
      } catch (error) {
        console.error(`Error creando ${nombre}:`, error);
        errores.push(nombre);
      }
    }

    setCreando(false);
    setInmobiliariasCreadas(creadas);

    if (errores.length === 0) {
      Swal.fire({
        icon: 'success',
        title: '¡Inmobiliarias creadas!',
        html: `Se crearon <strong>${creadas.length}</strong> inmobiliarias exitosamente.`,
        confirmButtonColor: '#0c2339'
      }).then(() => {
        // Recargar la página para actualizar los datos
        window.location.reload();
      });
    } else {
      Swal.fire({
        icon: 'warning',
        title: 'Proceso completado con errores',
        html: `Creadas: <strong>${creadas.length}</strong><br>Errores: <strong>${errores.length}</strong><br><br>
               Inmobiliarias con error:<br>${errores.join('<br>')}`,
        confirmButtonColor: '#0c2339'
      });
    }
  };

  useEffect(() => {
    if (!loading && (!user || user.rol !== 'ADMIN')) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const cargarInmobiliarias = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'inmobiliaria'));
        const datos: InmobiliariaFS[] = snapshot.docs.map(doc => ({
          id: doc.id,
          nombre: doc.data().nombre || '',
          correo: doc.data().correo || '',
          telefono: doc.data().telefono || ''
        }));
        setInmobiliariasFS(datos);

        // Verificar cada agencia nueva
        const verificaciones: ResultadoVerificacion[] = AGENCIAS_NUEVOS_MIEMBROS.map(agencia => {
          // Buscar match exacto (normalizado)
          const matchExacto = datos.find(inmo => normalizar(inmo.nombre) === normalizar(agencia));

          if (matchExacto) {
            return {
              agenciaNueva: agencia,
              existe: true,
              matchExacto: true,
              matchParcial: false,
              inmobiliariaEncontrada: matchExacto
            };
          }

          // Buscar matches parciales/similares
          const similares = datos
            .map(inmo => ({ ...inmo, similitud: calcularSimilitud(inmo.nombre, agencia) }))
            .filter(inmo => inmo.similitud >= 0.4)
            .sort((a, b) => b.similitud - a.similitud)
            .slice(0, 3);

          if (similares.length > 0 && similares[0].similitud >= 0.7) {
            return {
              agenciaNueva: agencia,
              existe: true,
              matchExacto: false,
              matchParcial: true,
              inmobiliariaEncontrada: similares[0],
              similares: similares
            };
          }

          return {
            agenciaNueva: agencia,
            existe: false,
            matchExacto: false,
            matchParcial: similares.length > 0,
            similares: similares.length > 0 ? similares : undefined
          };
        });

        setResultados(verificaciones);
      } catch (error) {
        console.error('Error al cargar inmobiliarias:', error);
      } finally {
        setCargando(false);
      }
    };

    if (user?.rol === 'ADMIN') {
      cargarInmobiliarias();
    }
  }, [user]);

  if (loading || cargando) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando...</span>
        </div>
      </div>
    );
  }

  const existentesExactas = resultados.filter(r => r.matchExacto);
  const existentesParciales = resultados.filter(r => r.matchParcial && !r.matchExacto);
  const nuevas = resultados.filter(r => !r.existe);
  const conSimilares = resultados.filter(r => !r.existe && r.similares && r.similares.length > 0);

  const resultadosFiltrados = filtro === 'todos' ? resultados
    : filtro === 'nuevas' ? nuevas
    : filtro === 'existentes' ? [...existentesExactas, ...existentesParciales]
    : conSimilares;

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h1>Verificación de Inmobiliarias - Nuevos Miembros Nov 2025</h1>

      {/* Resumen */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body text-center">
              <h3>{existentesExactas.length}</h3>
              <p className="mb-0">Match Exacto</p>
              <small>Ya existen en la plataforma</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-warning">
            <div className="card-body text-center">
              <h3>{existentesParciales.length}</h3>
              <p className="mb-0">Match Parcial</p>
              <small>Posible duplicado (verificar)</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body text-center">
              <h3>{nuevas.length}</h3>
              <p className="mb-0">Nuevas</p>
              <small>Crear en plataforma</small>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body text-center">
              <h3>{inmobiliariasFS.length}</h3>
              <p className="mb-0">Total en BD</p>
              <small>Inmobiliarias actuales</small>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="btn-group mb-4">
        <button
          className={`btn ${filtro === 'todos' ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setFiltro('todos')}
        >
          Todos ({resultados.length})
        </button>
        <button
          className={`btn ${filtro === 'existentes' ? 'btn-success' : 'btn-outline-success'}`}
          onClick={() => setFiltro('existentes')}
        >
          Existentes ({existentesExactas.length + existentesParciales.length})
        </button>
        <button
          className={`btn ${filtro === 'nuevas' ? 'btn-danger' : 'btn-outline-danger'}`}
          onClick={() => setFiltro('nuevas')}
        >
          Nuevas ({nuevas.length})
        </button>
        <button
          className={`btn ${filtro === 'similares' ? 'btn-warning' : 'btn-outline-warning'}`}
          onClick={() => setFiltro('similares')}
        >
          Con Similares ({conSimilares.length})
        </button>
      </div>

      {/* Tabla de resultados */}
      <div className="table-responsive">
        <table className="table table-striped table-hover">
          <thead className="table-dark">
            <tr>
              <th>#</th>
              <th>Agencia (Excel Nov 2025)</th>
              <th>Estado</th>
              <th>Match en Firestore</th>
              <th>Acción Sugerida</th>
            </tr>
          </thead>
          <tbody>
            {resultadosFiltrados.map((r, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td><strong>{r.agenciaNueva}</strong></td>
                <td>
                  {r.matchExacto && (
                    <span className="badge bg-success">Match Exacto</span>
                  )}
                  {r.matchParcial && !r.matchExacto && (
                    <span className="badge bg-warning text-dark">Match Parcial</span>
                  )}
                  {!r.existe && !r.similares && (
                    <span className="badge bg-danger">Nueva</span>
                  )}
                  {!r.existe && r.similares && r.similares.length > 0 && (
                    <span className="badge bg-info">Nueva (con similares)</span>
                  )}
                </td>
                <td>
                  {r.inmobiliariaEncontrada && (
                    <div>
                      <strong>{r.inmobiliariaEncontrada.nombre}</strong>
                      <br />
                      <small className="text-muted">ID: {r.inmobiliariaEncontrada.id}</small>
                    </div>
                  )}
                  {r.similares && r.similares.length > 0 && !r.inmobiliariaEncontrada && (
                    <div>
                      <small className="text-muted">Posibles matches:</small>
                      <ul className="mb-0 ps-3">
                        {r.similares.map((s, i) => (
                          <li key={i}>
                            <small>{s.nombre}</small>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!r.inmobiliariaEncontrada && (!r.similares || r.similares.length === 0) && (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {r.matchExacto && (
                    <span className="text-success">
                      <i className="fa-solid fa-check me-1"></i>
                      Usar existente
                    </span>
                  )}
                  {r.matchParcial && !r.matchExacto && (
                    <span className="text-warning">
                      <i className="fa-solid fa-exclamation-triangle me-1"></i>
                      Verificar manualmente
                    </span>
                  )}
                  {!r.existe && (
                    <span className="text-danger">
                      <i className="fa-solid fa-plus me-1"></i>
                      Crear nueva
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resumen para crear */}
      {filtro === 'nuevas' && nuevas.length > 0 && (
        <div className="card mt-4 border-danger">
          <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <i className="fa-solid fa-building me-2"></i>
              Inmobiliarias a Crear ({nuevas.length})
            </h5>
            <button
              className="btn btn-light btn-sm"
              onClick={crearInmobiliariasNuevas}
              disabled={creando}
            >
              {creando ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Creando {progreso.actual}/{progreso.total}...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-plus me-2"></i>
                  Crear Todas
                </>
              )}
            </button>
          </div>
          <div className="card-body">
            <p>Las siguientes inmobiliarias NO existen en la plataforma y deben crearse:</p>
            {creando && (
              <div className="progress mb-3">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{ width: `${(progreso.actual / progreso.total) * 100}%` }}
                >
                  {progreso.actual}/{progreso.total}
                </div>
              </div>
            )}
            <div className="row">
              {nuevas.map((r, idx) => (
                <div key={idx} className="col-md-4 mb-2">
                  <span className={`badge me-2 ${inmobiliariasCreadas.includes(r.agenciaNueva) ? 'bg-success' : 'bg-light text-dark'}`}>
                    {inmobiliariasCreadas.includes(r.agenciaNueva) ? '✓' : idx + 1}
                  </span>
                  {r.agenciaNueva}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Botón para exportar */}
      <div className="mt-4">
        <button
          className="btn btn-outline-primary me-2"
          onClick={() => {
            const data = {
              fecha: new Date().toISOString(),
              totalEnBD: inmobiliariasFS.length,
              totalVerificadas: resultados.length,
              matchExacto: existentesExactas.map(r => ({
                agencia: r.agenciaNueva,
                idFirestore: r.inmobiliariaEncontrada?.id
              })),
              matchParcial: existentesParciales.map(r => ({
                agencia: r.agenciaNueva,
                posibleMatch: r.inmobiliariaEncontrada?.nombre
              })),
              nuevas: nuevas.map(r => r.agenciaNueva)
            };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'verificacion-inmobiliarias.json';
            a.click();
          }}
        >
          <i className="fa-solid fa-download me-2"></i>
          Exportar Resultados (JSON)
        </button>

        <button
          className="btn btn-outline-secondary"
          onClick={() => navigate('/dashboard')}
        >
          <i className="fa-solid fa-arrow-left me-2"></i>
          Volver al Dashboard
        </button>
      </div>
    </div>
  );
}
